/**
 * Support-level vocabulary.
 *
 * Directive §6 defines the classification set; §19 forbids labelling incomplete
 * support as full support. These levels are the single source of truth: the
 * feasibility matrix in `docs/` is *generated* from the catalog that uses them,
 * and the runtime file-information screen (§31) reads the same values. A format
 * therefore cannot be documented as more capable than it is registered.
 */

/** §6 "Classify each format using the following support levels". */
export type SupportLevel =
  /** Layout, fonts, tables, images and charts render at high fidelity. */
  | 'full-view'
  /** Renders faithfully enough to read and navigate; some advanced layout may differ. */
  | 'standard-view'
  /** Text and structure are extracted; original layout is not reproduced. */
  | 'text-view'
  /** Create, modify and delete essentially any element the format supports. */
  | 'full-edit'
  /** Modify existing content (text, cells, simple objects); no advanced authoring. */
  | 'basic-edit'
  /** Overlay annotations only; the underlying content is never rewritten. */
  | 'annotate-edit'
  /** Editing happens after conversion to another format; round-trip is lossy. */
  | 'convert-edit'
  /** Deliberately never writable by Modu Viewer. */
  | 'read-only'
  /** Implementation plausible but unproven; must not ship as supported. */
  | 'needs-validation'
  /** Only achievable via a paid SDK; blocked by the §32 cost model. */
  | 'commercial-license'
  /** Not supported. Shown to the user as such (§19, §34). */
  | 'unsupported';

/** Ordered from weakest to strongest, for comparisons and UI sorting. */
export const VIEW_LEVEL_ORDER: readonly SupportLevel[] = [
  'unsupported', 'commercial-license', 'needs-validation',
  'text-view', 'standard-view', 'full-view',
] as const;

export const EDIT_LEVEL_ORDER: readonly SupportLevel[] = [
  'unsupported', 'commercial-license', 'needs-validation', 'read-only',
  'convert-edit', 'annotate-edit', 'basic-edit', 'full-edit',
] as const;

/** True when the level means "the user can actually see content". */
export function canView(level: SupportLevel): boolean {
  return level === 'full-view' || level === 'standard-view' || level === 'text-view';
}

/** True when the level means "the user can actually change something". */
export function canEdit(level: SupportLevel): boolean {
  return level === 'full-edit' || level === 'basic-edit'
    || level === 'annotate-edit' || level === 'convert-edit';
}

/**
 * True when the user must be warned before entering edit mode (§13, §16, §17).
 * Conversion-based editing cannot guarantee a lossless round-trip.
 */
export function editNeedsConversionWarning(level: SupportLevel): boolean {
  return level === 'convert-edit';
}

/** Fidelity of a single document aspect. `n/a` = the format has no such concept. */
export type Fidelity = 'high' | 'medium' | 'low' | 'none' | 'n/a';

/** §6 "Local processing feasibility" / "Server-conversion requirement". */
export type Processing =
  /** Fully local on every platform. The default and the §4.6 requirement. */
  | 'local'
  /** Local on some platforms, needs a native/OS decoder on others. */
  | 'local-native-assist'
  /** Cannot be done locally today; would need an opt-in server round trip (§4.5). */
  | 'server-required';

/** Risk that a large instance of this format degrades or breaks the app (§33). */
export type LargeFileRisk = 'low' | 'medium' | 'high';

export type FormatFamily =
  | 'document' | 'spreadsheet' | 'presentation' | 'image'
  | 'video' | 'audio' | 'archive' | 'design' | 'code' | 'data';

/** Per-platform support, as required by §6's matrix columns. */
export interface PlatformSupport {
  readonly android: readonly [view: SupportLevel, edit: SupportLevel];
  readonly ios: readonly [view: SupportLevel, edit: SupportLevel];
  readonly windows: readonly [view: SupportLevel, edit: SupportLevel];
  readonly macos: readonly [view: SupportLevel, edit: SupportLevel];
  readonly web: readonly [view: SupportLevel, edit: SupportLevel];
}

export interface FidelityProfile {
  readonly layout: Fidelity;
  readonly fonts: Fidelity;
  readonly tables: Fidelity;
  readonly images: Fidelity;
  readonly charts: Fidelity;
  readonly formulas: Fidelity;
}

export interface LibraryRef {
  readonly name: string;
  readonly version: string;
  readonly license: string;
  /** What this library is used for in this format's pipeline. */
  readonly role: string;
}

/** One row of the §6 technical feasibility matrix. */
export interface FormatSpec {
  readonly id: string;
  readonly label: string;
  /** Lower-case, dot-prefixed. First entry is canonical. */
  readonly extensions: readonly string[];
  /** First entry is canonical; others are seen in the wild. */
  readonly mimeTypes: readonly string[];
  readonly family: FormatFamily;
  /** Official specification reference (§6). */
  readonly spec: string;
  /** Rollout phase per §8. `null` = evaluated but not scheduled. */
  readonly phase: 1 | 2 | 3 | null;

  /**
   * Headline support level — the **best** level reached on any platform, and an
   * upper bound on every entry in {@link platforms} (enforced by test).
   *
   * It is what the format list and the feasibility matrix headline, never a
   * promise for the platform in front of the user. Runtime code that decides
   * what someone can actually do must read {@link platforms} for the current
   * platform; the generated matrix prints both columns side by side so the gap
   * is always visible rather than implied (§19).
   */
  readonly view: SupportLevel;
  readonly edit: SupportLevel;
  readonly platforms: PlatformSupport;

  readonly processing: Processing;
  readonly fidelity: FidelityProfile;

  /** Format supports password protection AND we handle it. */
  readonly passwordSupport: 'handled' | 'detected-not-handled' | 'n/a';
  /** Format supports encryption AND we handle it. */
  readonly encryptionSupport: 'handled' | 'detected-not-handled' | 'n/a';

  readonly largeFileRisk: LargeFileRisk;
  /** Human-readable size beyond which we degrade to a limited mode. */
  readonly softSizeLimit: string;

  /** §13/§24: features that may be lost on save. Empty = lossless round-trip. */
  readonly lossOnSave: readonly string[];
  readonly libraries: readonly LibraryRef[];
  /** §30: threats this format carries. */
  readonly securityRisks: readonly string[];
  readonly notes: string;
}
